import {
  BaseResponsePaginationProps,
  BaseResponseProps,
  HeaderSection,
  generateFormRules,
  generateQueryString,
  getStandardError,
} from '@qlibs/react-components';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Grid,
  Input,
  Row,
  Select,
  Switch,
  Upload,
  message,
} from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import {
  CustomFormItemPer1,
  CustomFormItemPer2,
  CustomFormItemPer3,
} from '../user/components/CustomFormItem';
import useAddress from '../../hooks/useAddress';
import { httpRequest, httpRequestMultipart } from '../../helpers/api';
import {
  DeviceProperties,
  EDeviceType,
  ICreateDevice,
  resolveOriginalDeviceAssetId,
} from './types/device.type';
import SectionContent from '../../components/SectionContent';
import { AssetProperties } from '../asset/types/asset.type';
import { UploadOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import { DeviceTypeProperties } from '../deviceType/types/device.type';
import { CompanyProperties } from '../company/types/company.type';
import { GroupProperties } from '../group/types/group.type';
import {
  DeviceMobileActionButton,
  DeviceMobileAlert,
  DeviceMobileField,
  DeviceMobileForm,
  DeviceMobileHeaderActions,
  DeviceMobilePage,
  DeviceMobileResultCard,
  DeviceMobileSecondaryButton,
  DeviceMobileSection,
  DeviceMobileSectionHeader,
  DeviceMobileSwitchField,
  DeviceMobileWarningCard,
  MobileDeviceHeader,
} from './mobile';

export enum EAEFuelType {
  FUEL_CONSTANTA = 'fuel-constanta',
  FUEL_CALCULATED = 'fuel-calculated',
}

const DeviceCreate = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [isLoadingAction, setIsLoadingAction] = React.useState(false);
  const [form] = Form.useForm();
  const [assets, setAssets] = React.useState<AssetProperties[]>([]);
  const [companies, setCompanies] = React.useState<CompanyProperties[]>([]);
  const [groups, setGroups] = React.useState<GroupProperties[]>([]);
  const [activeGroupId, setActiveGroupId] = React.useState<string>();
  const [activeCompanyId, setActiveCompanyId] = React.useState<string>();
  const [deviceTypes, setDeviceTypes] = React.useState<DeviceTypeProperties[]>(
    []
  );
  const [deviceUniqueId, setDeviceUniqueId] = React.useState<{
    asset: string;
    deviceType: string;
    location: string;
    flowType: string;
  }>({
    asset: '',
    deviceType: '',
    location: '',
    flowType: '',
  });
  const [currentDeviceType, setCurrentDeviceType] = React.useState<string>();
  const [aeFuelType, setAEFuelType] = React.useState<string>();
  const isPanelDoorDevice =
    currentDeviceType?.trim().toLowerCase() === 'panel door';

  async function getDeviceTypes() {
    try {
      setIsLoadingAction(true);
      const res = await httpRequest.get<
        BaseResponsePaginationProps<DeviceTypeProperties>
      >('/device-type?filterStatus=active');

      if (res) {
        setDeviceTypes(res.data.payload.results);
      }
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  async function createDevice(values: ICreateDevice) {
    try {
      setIsLoadingAction(true);

      let metadata = {
        aeFuelType: values.aeFuelType,
        aeFuelConstanta: values.aeFuelConstanta
          ? Number(values.aeFuelConstanta)
          : null,
      };

      const selectedAsset = assets.find((item) => item.assetId === values.assetId);
      const formData = {
        name: values.name,
        uniqueId: deviceUniqueIdCombined,
        deviceTypeId: values.deviceType,
        assetId: resolveOriginalDeviceAssetId(selectedAsset, values.assetId),
        description: values.description,
        status: values.status ? 'active' : 'inactive',
        metadata,
      };

      await httpRequest.post<BaseResponseProps<DeviceProperties>>(
        '/device',
        formData
      );

      message.success(`Success add ${values.name}`);
      form.resetFields();
      backToList(navigate, '/device');
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  const handleSubmit = async (values: any) => {
    if (deviceUniqueIdCombined) {
      const ids = deviceUniqueIdCombined.split('-');

      if (currentDeviceType === 'flowmeter') {
        if (ids.length < 4) {
          message.warning('Flowmeter device unique id must be valid');
          return;
        }
      } else if (currentDeviceType!.includes('ae')) {
        if (!aeFuelType) {
          message.warning('AE Fuel Type must be selected');
          return;
        } else if (
          aeFuelType === EAEFuelType.FUEL_CALCULATED &&
          ids.length < 4
        ) {
          message.warning(
            `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid. Please set the flow type`
          );
          return;
        } else if (ids.length < 3) {
          message.warning(
            `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid`
          );
          return;
        } else if (
          aeFuelType === EAEFuelType.FUEL_CONSTANTA &&
          !values.aeFuelConstanta
        ) {
          message.warning(`AE Fuel Constanta must be filled`);
          return;
        }
      } else if (isPanelDoorDevice) {
        if (ids.length !== 3 || ids[1] !== 'dp' || ids[2] !== '1') {
          message.warning('Panel Door device unique id must use the format [asset]-dp-1');
          return;
        }
      } else if (['rpm', 'clutch'].includes(currentDeviceType!)) {
        if (ids.length < 3) {
          message.warning(
            `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid`
          );
          return;
        }
      } else {
        if (ids.length < 2) {
          message.warning(
            `${currentDeviceType!.toLocaleUpperCase()} device ID must be valid`
          );
          return;
        }
      }

      createDevice(values);
    } else {
      message.warning('Device Unique ID must be filled');
      return;
    }
  };

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        setIsLoadingAction(true);
        const response = await httpRequest.get<
          BaseResponsePaginationProps<AssetProperties>
        >(
          `/asset${generateQueryString({
            filterCompanyIds: activeCompanyId,
          })}`
        );

        if (response.data.payload) {
          setAssets(response.data.payload.results);
        }
      } catch (error) {
        message.error(`Failed get data assets`);
      } finally {
        setIsLoadingAction(false);
      }
    };

    if (activeCompanyId) {
      fetchAsset();
    }
    getDeviceTypes();
  }, [activeCompanyId]);

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        setIsLoadingAction(true);
        const response = await httpRequest.get<
          BaseResponsePaginationProps<GroupProperties>
        >('/group');

        if (response.data.payload) {
          const resGroups = response.data.payload.results;
          setGroups(resGroups);

          let allCompanies: CompanyProperties[] = [];
          resGroups.forEach((item) => {
            if (item.companies) {
              allCompanies = [...allCompanies, ...item.companies];
            }
          });

          setCompanies(allCompanies);
        }
      } catch (error) {
        message.error(`Failed get data groups`);
      } finally {
        setIsLoadingAction(false);
      }
    };

    fetchGroup();
  }, []);

  const groupMapped = React.useMemo(() => {
    return groups.map((item) => ({
      value: item.groupId,
      label: item.name,
    }));
  }, [groups]);

  const companyMapped = React.useMemo(() => {
    return companies
      .map((item) => ({
        value: item.companyId,
        label: item.name,
        groupId: item.groupId,
      }))
      .filter((item) => item.groupId === activeGroupId);
  }, [companies, activeGroupId]);

  const assetMapped = React.useMemo(() => {
    return assets
      .map((item) => ({
        value: item.assetId,
        label: item.name,
        companyId: item.companyId,
      }))
      .filter((item) => item.companyId === activeCompanyId);
  }, [assets, activeCompanyId]);

  const deviceTypeMapped = React.useMemo(() => {
    return Object.values(deviceTypes).map((item) => ({
      label: item.name,
      value: item.deviceTypeId,
    }));
  }, [deviceTypes]);

  const deviceUniqueIdCombined = useMemo(() => {
    if (deviceUniqueId) {
      return Object.values(deviceUniqueId)
        .filter((item) => item)
        .join('-');
    }
  }, [deviceUniqueId]);

  const prefix = useMemo(() => {
    return deviceUniqueId?.asset + '-' + deviceUniqueId?.deviceType;
  }, [deviceUniqueId]);

  function handleChangeGroup(e: string) {
    const found = groupMapped.find((item) => item.value === e);
    if (found) {
      setActiveGroupId(found.value);
      form.setFieldsValue({
        companyId: undefined,
        assetId: undefined,
      });
      setDeviceUniqueId({
        asset: '',
        deviceType: '',
        location: '',
        flowType: '',
      });
    }
  }

  function handleChangeCompany(e: string) {
    const found = companyMapped.find((item) => item.value === e);
    if (found) {
      setActiveCompanyId(found.value);
      form.setFieldsValue({
        assetId: undefined,
      });
      setDeviceUniqueId({
        asset: '',
        deviceType: '',
        location: '',
        flowType: '',
      });
    }
  }

  function handleChangeAsset(e: string) {
    const found = assetMapped.find((item) => item.value === e);
    if (found) {
      setDeviceUniqueId((current) => ({
        ...current,
        asset: found.label.replace(/[^a-zA-Z0-9]/g, ''),
      }));
    }
  }

  function resetTmpData() {
    setDeviceUniqueId((current) => ({
      ...current,
      deviceType: '',
      location: '',
      flowType: '',
    }));
    setCurrentDeviceType(undefined);
    setAEFuelType(undefined);
    form.setFieldsValue({
      aeFuelType: undefined,
      aeFuelConstanta: undefined,
    });
  }

  function handleChangeDeviceType(e: string) {
    const found = deviceTypeMapped.find((item) => item.value === e);

    resetTmpData();

    if (found) {
      setCurrentDeviceType(found.label.toLocaleLowerCase());
      if (found.label.trim().toLocaleLowerCase() === 'panel door') {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: 'dp',
          location: '1',
          flowType: '',
        }));
      } else if (found.label.toLocaleLowerCase() === 'flowmeter') {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: 'fm',
        }));
      } else if (
        ['rpm', 'clutch', 'ae'].includes(found.label.toLocaleLowerCase())
      ) {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: found.label.toLocaleLowerCase(),
          flowType: '',
        }));
      } else {
        setDeviceUniqueId((current) => ({
          ...current,
          deviceType: found.label.toLocaleLowerCase(),
          location: '',
          flowType: '',
        }));
      }
    }
  }

  function handleChangeAEFuelType(e: string) {
    setAEFuelType(e);
    if (e === EAEFuelType.FUEL_CONSTANTA) {
      setDeviceUniqueId((current) => ({
        ...current,
        flowType: '',
      }));
    }
  }

  const renderDeviceUniqueID = () => {
    if (currentDeviceType === EDeviceType.FLOWMETER) {
      return (
        <Row>
          <CustomFormItemPer3 label='Prefix'>
            <Input value={prefix} disabled />
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Location'>
            <Select
              value={deviceUniqueId?.location}
              placeholder='Choose asset'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
              options={[
                {
                  label: 'PORT',
                  value: '1',
                },
                {
                  label: 'STARBOARD',
                  value: '2',
                },
                {
                  label: 'CENTER',
                  value: '3',
                },
              ]}
            />
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Flow type'>
            <Select
              placeholder='Choose asset'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
              value={deviceUniqueId?.flowType}
              options={[
                {
                  label: 'IN',
                  value: '1',
                },
                {
                  label: 'OUT',
                  value: '2',
                },
              ]}
            />
          </CustomFormItemPer3>
        </Row>
      );
    } else if (
      currentDeviceType?.toLowerCase() &&
      currentDeviceType?.toLowerCase() === EDeviceType.BUNKER
    ) {
      return (
        <Row>
          <CustomFormItemPer3 label='Prefix'>
            <div>{prefix}</div>
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Flow type'>
            <Select
              placeholder='Choose flow type'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
              value={deviceUniqueId?.flowType}
              options={[
                {
                  label: 'IN',
                  value: '1',
                },
                {
                  label: 'OUT',
                  value: '2',
                },
              ]}
            />
          </CustomFormItemPer3>
        </Row>
      );
    } else if (
      currentDeviceType &&
      ['rpm', 'clutch'].includes(currentDeviceType)
    ) {
      return (
        <Row>
          <CustomFormItemPer3 label='Prefix'>
            <Input value={prefix} disabled />
          </CustomFormItemPer3>
          <CustomFormItemPer3 label='Location'>
            <Select
              value={deviceUniqueId?.location}
              placeholder='Choose asset'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
              options={[
                {
                  label: 'PORT',
                  value: '1',
                },
                {
                  label: 'STARBOARD',
                  value: '2',
                },
                {
                  label: 'CENTER',
                  value: '3',
                },
              ]}
            />
          </CustomFormItemPer3>
        </Row>
      );
    } else if (
      currentDeviceType &&
      currentDeviceType.includes(EDeviceType.AE) &&
      aeFuelType === EAEFuelType.FUEL_CALCULATED
    ) {
      return (
        <Row>
          <CustomFormItemPer2 label='Prefix'>
            <Input value={prefix} disabled />
          </CustomFormItemPer2>
          <CustomFormItemPer2 label='Flow type'>
            <Select
              placeholder='Choose asset'
              style={{ width: '100%' }}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
              value={deviceUniqueId?.flowType}
              options={[
                {
                  label: 'IN',
                  value: '1',
                },
                {
                  label: 'OUT',
                  value: '2',
                },
              ]}
            />
          </CustomFormItemPer2>
        </Row>
      );
    } else {
      return;
    }
  };

  const renderMobileDeviceUniqueID = () => {
    if (currentDeviceType === EDeviceType.FLOWMETER) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Location">
            <Select
              value={deviceUniqueId?.location}
              placeholder="Choose location"
              options={[
                { label: 'PORT', value: '1' },
                { label: 'STARBOARD', value: '2' },
                { label: 'CENTER', value: '3' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
            />
          </DeviceMobileField>
          <DeviceMobileField label="Flow type">
            <Select
              value={deviceUniqueId?.flowType}
              placeholder="Choose flow type"
              options={[
                { label: 'IN', value: '1' },
                { label: 'OUT', value: '2' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    if (
      currentDeviceType?.toLowerCase() &&
      currentDeviceType?.toLowerCase() === EDeviceType.BUNKER
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Flow type">
            <Select
              value={deviceUniqueId?.flowType}
              placeholder="Choose flow type"
              options={[
                { label: 'IN', value: '1' },
                { label: 'OUT', value: '2' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    if (
      currentDeviceType &&
      ['rpm', 'clutch'].includes(currentDeviceType)
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Location">
            <Select
              value={deviceUniqueId?.location}
              placeholder="Choose location"
              options={[
                { label: 'PORT', value: '1' },
                { label: 'STARBOARD', value: '2' },
                { label: 'CENTER', value: '3' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, location: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    if (
      currentDeviceType &&
      currentDeviceType.includes(EDeviceType.AE) &&
      aeFuelType === EAEFuelType.FUEL_CALCULATED
    ) {
      return (
        <>
          <DeviceMobileField label="Prefix">
            <Input value={prefix} disabled />
          </DeviceMobileField>
          <DeviceMobileField label="Flow type">
            <Select
              value={deviceUniqueId?.flowType}
              placeholder="Choose flow type"
              options={[
                { label: 'IN', value: '1' },
                { label: 'OUT', value: '2' },
              ]}
              onChange={(e) =>
                setDeviceUniqueId((current) => ({ ...current, flowType: e }))
              }
            />
          </DeviceMobileField>
        </>
      );
    }

    return null;
  };

  const renderMobileContent = () => (
    <DeviceMobilePage>
      <MobileDeviceHeader
        title="Device"
        onBack={() => backToList(navigate, '/device')}
        action={
          <DeviceMobileHeaderActions>
            <DeviceMobileSecondaryButton onClick={() => backToList(navigate, '/device')}>
              Cancel
            </DeviceMobileSecondaryButton>
            <DeviceMobileActionButton
              type="primary"
              loading={isLoadingAction}
              onClick={() => form.submit()}
            >
              Save
            </DeviceMobileActionButton>
          </DeviceMobileHeaderActions>
        }
      />

      <DeviceMobileForm>
        <Form
          form={form}
          name="profileForm"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <DeviceMobileSection $bordered>
            <DeviceMobileSectionHeader>
              <h2>Information</h2>
              <p>Detail information about device</p>
            </DeviceMobileSectionHeader>
            <DeviceMobileField
              label="Group"
              name="groupId"
              rules={generateFormRules('Group', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={isLoadingAction}
                showSearch
                placeholder="Choose group"
                options={groupMapped}
                onChange={handleChangeGroup}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Company"
              name="companyId"
              rules={generateFormRules('Company', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={isLoadingAction}
                showSearch
                placeholder="Choose company"
                options={companyMapped}
                onChange={handleChangeCompany}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Asset"
              name="assetId"
              rules={generateFormRules('Asset', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={isLoadingAction}
                showSearch
                placeholder="Choose asset"
                options={assetMapped}
                onChange={handleChangeAsset}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Device Type"
              name="deviceType"
              rules={generateFormRules('Device Type', ['required'])}
            >
              <Select
                loading={isLoadingAction}
                disabled={isLoadingAction}
                showSearch
                placeholder="Choose device type"
                options={deviceTypeMapped}
                onChange={handleChangeDeviceType}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </DeviceMobileField>
            <DeviceMobileField
              label="Device Name"
              name="name"
              rules={generateFormRules('Device Name', ['required'])}
            >
              <Input placeholder="Input device name" />
            </DeviceMobileField>
            <DeviceMobileField label="Description" name="description">
              <Input placeholder="Input device description" />
            </DeviceMobileField>
            {currentDeviceType && currentDeviceType.includes('ae') && (
              <>
                <DeviceMobileField label="AE Fuel Type" name="aeFuelType">
                  <Select
                    loading={isLoadingAction}
                    disabled={isLoadingAction}
                    showSearch
                    placeholder="Choose device type"
                    options={Object.values(EAEFuelType).map((item) => ({
                      label: item.replace(/-/g, ' ').toUpperCase(),
                      value: item,
                    }))}
                    onChange={handleChangeAEFuelType}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </DeviceMobileField>
                {currentDeviceType?.toLowerCase() &&
                  currentDeviceType
                    ?.toLowerCase()
                    .includes(EDeviceType.AE) &&
                  aeFuelType === EAEFuelType.FUEL_CONSTANTA && (
                    <DeviceMobileField
                      label="AE Fuel Constanta"
                      name="aeFuelConstanta"
                    >
                      <Input
                        placeholder="Input AE fuel constanta"
                        suffix="L/Min"
                      />
                    </DeviceMobileField>
                  )}
                <DeviceMobileAlert>
                  <Alert
                    type="warning"
                    showIcon
                    description={
                      <>
                        <div>
                          AE Fuel Constanta only required when AE Fuel Type is
                          set to Fuel Constanta
                        </div>
                        <div>
                          If you choose fuel calculated then you need to set the
                          flow
                        </div>
                      </>
                    }
                  />
                </DeviceMobileAlert>
              </>
            )}
          </DeviceMobileSection>

          <DeviceMobileSection $bordered>
            <DeviceMobileSectionHeader>
              <h2>Device Unique ID</h2>
              <p>Detail information about device</p>
            </DeviceMobileSectionHeader>
            {renderMobileDeviceUniqueID()}
            <DeviceMobileResultCard>
              Result Device Unique ID: {deviceUniqueIdCombined}
            </DeviceMobileResultCard>
            <DeviceMobileWarningCard>
              Please makesure this device unique id match with ID on IOT.
              Otherwise the report data won't work
            </DeviceMobileWarningCard>
          </DeviceMobileSection>

          <DeviceMobileSection>
            <DeviceMobileSectionHeader>
              <h2>Setting</h2>
              <p>These are device information, you can change anything</p>
            </DeviceMobileSectionHeader>
            <DeviceMobileSwitchField
              label="Status"
              name="status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch defaultChecked className="custom-switch" />
            </DeviceMobileSwitchField>
          </DeviceMobileSection>
        </Form>
      </DeviceMobileForm>
    </DeviceMobilePage>
  );

  if (isMobile) {
    return renderMobileContent();
  }

  return (
    <React.Fragment>
      <HeaderSection
        icon='back'
        title={'Add Device'}
        subtitle={'Create new device'}
        showCancelButton
        showSaveButton
        isLoading={isLoadingAction}
        onSave={() => form.submit()}
      />

      <Card>
        <Form
          form={form}
          name='profileForm'
          layout='vertical'
          onFinish={handleSubmit}
          autoComplete='off'
        >
          <SectionContent
            groupTitle='Information'
            subTitle='Detail information about device'
          >
            <Row>
              <CustomFormItemPer1
                label='Group'
                name='groupId'
                rules={generateFormRules('Group', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                  showSearch
                  placeholder='Choose group'
                  style={{ width: '100%' }}
                  options={groupMapped}
                  onChange={handleChangeGroup}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer1>
            </Row>
            <Row>
              <CustomFormItemPer2
                label='Company'
                name='companyId'
                rules={generateFormRules('Company', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                  showSearch
                  placeholder='Choose company'
                  style={{ width: '100%' }}
                  options={companyMapped}
                  onChange={handleChangeCompany}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='Asset'
                name='assetId'
                rules={generateFormRules('Asset', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                  showSearch
                  placeholder='Choose asset'
                  style={{ width: '100%' }}
                  options={assetMapped}
                  onChange={handleChangeAsset}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer2
                label='Device Type'
                name='deviceType'
                rules={generateFormRules('Device Type', ['required'])}
              >
                <Select
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                  showSearch
                  placeholder='Choose device type'
                  style={{ width: '100%' }}
                  options={deviceTypeMapped}
                  onChange={handleChangeDeviceType}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label='Device Name'
                name='name'
                rules={generateFormRules('Device Name', ['required'])}
              >
                <Input placeholder='Input device name' />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer1 label='Description' name='description'>
                <Input placeholder='Input device description' />
              </CustomFormItemPer1>
            </Row>
            {currentDeviceType && currentDeviceType.includes('ae') && (
              <Row>
                <CustomFormItemPer2 label='AE Fuel Type' name='aeFuelType'>
                  <Select
                    loading={isLoadingAction}
                    disabled={isLoadingAction}
                    showSearch
                    placeholder='Choose device type'
                    style={{ width: '100%' }}
                    options={Object.values(EAEFuelType).map((item) => ({
                      label: item.replace(/-/g, ' ').toUpperCase(),
                      value: item,
                    }))}
                    onChange={handleChangeAEFuelType}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </CustomFormItemPer2>
                {currentDeviceType?.toLowerCase() &&
                  currentDeviceType?.toLowerCase().includes(EDeviceType.AE) &&
                  aeFuelType === EAEFuelType.FUEL_CONSTANTA && (
                    <CustomFormItemPer2
                      label='AE Fuel Constanta'
                      name='aeFuelConstanta'
                    >
                      <Input
                        placeholder='Input AE fuel constanta'
                        suffix='L/Min'
                      />
                    </CustomFormItemPer2>
                  )}

                {currentDeviceType?.toLowerCase() &&
                  currentDeviceType?.toLowerCase().includes(EDeviceType.AE) && (
                    <CustomFormItemPer1>
                      <Alert
                        type='warning'
                        showIcon
                        description={
                          <>
                            <div>
                              AE Fuel Constanta only required when AE Fuel Type
                              is set to Fuel Constanta
                            </div>
                            <div>
                              If you choose fuel calculated then you need to set
                              the flow
                            </div>
                          </>
                        }
                      />
                    </CustomFormItemPer1>
                  )}
              </Row>
            )}
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle='Device Unique ID'
            subTitle='Detail information about device'
          >
            {renderDeviceUniqueID()}
            <Row>
              <CustomFormItemPer1>
                <Alert
                  message={
                    <b>Result Device Unique ID: {deviceUniqueIdCombined}</b>
                  }
                />
              </CustomFormItemPer1>
              <CustomFormItemPer1>
                <Alert
                  type='warning'
                  showIcon
                  message={`Please makesure this device unique id match with ID on IOT. Otherwise the report data won't work`}
                />
              </CustomFormItemPer1>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle={`Setting`}
            subTitle='These are device information, you can change anything'
          >
            <CustomFormItemPer1
              label='Status'
              name='status'
              valuePropName='checked'
              initialValue={true}
            >
              <Switch defaultChecked className='custom-switch' />
            </CustomFormItemPer1>
          </SectionContent>
        </Form>
      </Card>
    </React.Fragment>
  );
};

export default DeviceCreate;
